import cookieManager from './cookieManager'

/**
 * Checks if the current environment is a production environment.
 * It considers 'localhost' and '.vercel.app' as non-production environments.
 *
 * @returns {boolean} true if the environment is production, false otherwise.
 */
export function isProduction () {
  const host = window.location.hostname

  return !(host === 'localhost' || host.includes('.vercel.app') || isStage())
}

export function isStage () {
  return window.location.hostname.includes('.stage.max.auto')
}

export function isStageAllowedDealer (hostname) {
  const allowedDealerPatterns = [/^showroom$/g, /windy/g, /maxqa/g]

  const hostPrefix = hostname.replace('.stage.max.auto', '')

  return allowedDealerPatterns.some(dealerPattern => dealerPattern.test(hostPrefix))
}

/**
 * Gets the host name of the current dealership or the dealership selection screen
 *
 * We rely on the domain to define the instance:
 * - Production: uses the dealership's actual hostname (e.g., windy.max.auto)
 * - Stage: converts the stage hostname (e.g., windy.stage.max.auto) to production format (e.g., windy.max.auto)
 * - Local or preview environments: uses a single domain that points to one instance ('windy.max.auto')
 *
 * During testing you can change the instance dynamically without re-deploying by setting the `com.max.path.instance` cookie in your browser locally
 *
 * @returns {string} The host name
 */
export function getHost () {
  let host = window.location.hostname

  if (isProduction()) return host

  if (isStage()) {
    if (!isStageAllowedDealer(host)) {
      throw new Error(
        `Requested stage hostname(${host}) is not allowed.`
      )
    }

    host = host.replace('.stage.max.auto', '.max.auto')
    return host
  }

  host = cookieManager.get('com.max.path.instance') || 'windy.max.auto'

  return host
}

/**
 * Navigate to the dealership hostname or to the dealership selection screen.
 * In production, it sets the window location to the hostname.
 * In stage, it sets the stage version of the dealership hostname.
 * In development, it sets a cookie with the hostname and redirects to home.
 *
 * @param {string} dealershipHostname - The hostname of the dealership
 */
export function setHost (dealershipHostname) {
  if (isProduction()) {
    window.location = `https://${dealershipHostname}`
    return
  }

  if (isStage()) {
    const dealershipStageHostname = dealershipHostname.replace('.max.auto', '.stage.max.auto')
    window.location = `https://${dealershipStageHostname}`
    return
  }

  cookieManager.set('com.max.path.instance', dealershipHostname)
  window.location.pathname = '/'
}
